import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, Headphones, Home, Sparkles } from 'lucide-react';
import { BRAND_NAME, BRAND_TAGLINE, logoImage } from '../../config/brand';
import { cardTheme } from '../../styles/cardTheme';

const FEATURES = [
  { icon: BookOpen, text: 'كورسات ومحاضرات لطلاب الثانوية الأزهرية' },
  { icon: GraduationCap, text: 'متابعة تقدمك من بوابة الطالب' },
  { icon: Headphones, text: 'دعم فني سريع عبر واتساب' },
];

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  alternateLink?: { prompt: string; label: string; to: string };
  footerNote?: string;
  wide?: boolean;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  alternateLink,
  footerNote,
  wide = false,
}) => (
  <div className="min-h-screen font-cairo bg-gradient-to-b from-green-50/80 via-white to-gold-50/40" dir="rtl">
    <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col lg:flex-row">
      {/* Brand panel — hidden on small screens, visible lg+ */}
      <aside className="relative hidden overflow-hidden lg:flex lg:w-[42%] xl:w-[44%]">
        <div className={`flex h-full w-full flex-col justify-between ${cardTheme.headerBanner} p-10 xl:p-12`}>
          <div className="absolute inset-0 opacity-[0.07]">
            <div className="absolute -left-16 top-20 h-64 w-64 rounded-full bg-gold-400 blur-3xl" />
            <div className="absolute -right-10 bottom-16 h-72 w-72 rounded-full bg-gold-500 blur-3xl" />
          </div>

          <div className="relative z-10">
            <Link
              to="/"
              className="mb-10 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm font-medium text-white/90 backdrop-blur-sm transition-colors hover:bg-white/15"
            >
              <Home className="h-4 w-4" />
              العودة للرئيسية
            </Link>

            <img
              src={logoImage}
              alt={BRAND_NAME}
              className="mb-6 h-16 w-auto object-contain logo-transparent brightness-0 invert"
            />
            <h2 className="mb-2 text-3xl font-bold text-white font-cairo-bold">
              {BRAND_NAME}
            </h2>
            <p className="mb-8 max-w-xs text-sm leading-relaxed text-green-100/90">
              {BRAND_TAGLINE}
            </p>

            <ul className="space-y-4">
              {FEATURES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold-500/20 text-gold-300">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm leading-relaxed text-green-50/95">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="relative z-10 text-xs text-green-200/70">
            محتوى تعليمي يليق بتميز طالب الثانوية الأزهرية
          </p>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex flex-1 flex-col justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-10 xl:px-14">
        {/* Mobile brand strip */}
        <div className="mb-6 text-center lg:hidden">
          <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-900">
            <Home className="h-4 w-4" />
            العودة للرئيسية
          </Link>
          <img
            src={logoImage}
            alt={BRAND_NAME}
            className="mx-auto mb-3 h-14 w-auto object-contain logo-transparent"
          />
          <span className={`inline-flex items-center gap-1.5 ${cardTheme.badgeGold}`}>
            <Sparkles className="h-3.5 w-3.5" />
            {BRAND_NAME}
          </span>
        </div>

        <div className={`mx-auto w-full ${wide ? 'max-w-xl' : 'max-w-md'}`}>
          <div className={`${cardTheme.shell} border-green-100/80 p-6 sm:p-8`}>
            <div className="mb-6 sm:mb-8">
              <h1 className={`mb-1.5 text-2xl font-bold sm:text-3xl ${cardTheme.title}`}>{title}</h1>
              <p className="text-sm text-gray-600 sm:text-base">{subtitle}</p>
              <div className={`mt-4 h-1 w-14 rounded-full ${cardTheme.divider}`} />
            </div>

            {children}

            {alternateLink && (
              <div className="mt-8 border-t border-green-100 pt-6 text-center">
                <p className="mb-3 text-sm text-gray-600">{alternateLink.prompt}</p>
                <Link
                  to={alternateLink.to}
                  className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold ${cardTheme.btnOutline}`}
                >
                  {alternateLink.label}
                </Link>
              </div>
            )}
          </div>

          {footerNote && (
            <p className="mt-5 text-center text-xs leading-relaxed text-gray-500">{footerNote}</p>
          )}
        </div>
      </main>
    </div>
  </div>
);

export default AuthLayout;
